/**
 * @fileoverview `@`-mention token parsing for the chat composer (MOR-890).
 *
 * Pure helpers kept out of ChatInput.tsx so they're unit-testable.
 */

export const MAX_MENTION_QUERY_CHARS = 60;

export interface ActiveMention {
  /** Index of the `@` in the composer value. */
  start: number;
  /** Text between the `@` and the caret. */
  query: string;
}

/**
 * Finds the active `@`-mention token: the nearest `@` before the caret
 * that starts a word (start-of-text or after whitespace), with no newline
 * between it and the caret. File names may contain spaces, so spaces are
 * allowed inside the query — the dropdown closes instead when nothing
 * matches or the user dismisses it.
 */
export function findActiveMention(
  value: string,
  caret: number,
): ActiveMention | null {
  const upto = value.slice(0, caret);
  const at = upto.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const query = upto.slice(at + 1);
  if (query.includes("\n") || query.length > MAX_MENTION_QUERY_CHARS)
    return null;
  return { start: at, query };
}

/** Minimal shape the mention filter needs (mirrors `ChatMentionItem`). */
interface MentionCandidate {
  id: string;
  name: string;
}

/**
 * The items the `@`-mention dropdown should offer for the active token.
 *
 * Drops an item once it is already tagged — either because the host attached
 * it (`attachedIds`) or because `@Name` is already present in the composer
 * text. The text check matters because spaces are allowed inside a query: a
 * completed multi-word tag like `@1.0 Introduction` still parses as an active
 * mention that matches its own item, which would otherwise keep the dropdown
 * open forever and swallow the Enter key instead of sending (MOR-924). Items
 * without an attachment chip (e.g. report sections) depend entirely on this.
 * Deleting the tag from the text makes the item available again.
 */
export function filterMentionItems<T extends MentionCandidate>(
  items: T[],
  query: string,
  opts: { text: string; attachedIds?: Iterable<string>; limit: number },
): T[] {
  const needle = query.trim().toLowerCase();
  const attached = new Set(opts.attachedIds ?? []);
  return items
    .filter((item) => !attached.has(item.id))
    .filter((item) => !opts.text.includes(`@${item.name}`))
    .filter(
      (item) => needle.length === 0 || item.name.toLowerCase().includes(needle),
    )
    .slice(0, opts.limit);
}
